// StoreForm Component
import React from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  TimePicker,
  Checkbox,
  Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { useSelector } from "react-redux";
import { apiHandle } from "../../config/apiHandle";
import { useLocation } from "react-router-dom";

const { Item } = Form;

const daysOptions = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const StoreForm = ({
  storeData,
  onFormChange,
  onFinish,
  loading,
  canUpdate,
  canCreate,
  idExist,
}) => {
  const location = useLocation();
  const { user } = useSelector((state) => state.userAuth);
  const [form] = Form.useForm();
  const [vendors, setVendors] = React.useState([]);
  const [loadingVendors, setLoadingVendors] = React.useState(false);

  // Fetch vendors when component mounts if user is Admin
  React.useEffect(() => {
    const fetchVendors = async () => {
      if (user?.role === "Admin") {
        try {
          setLoadingVendors(true);
          const response = await apiHandle.get("/get-vendors");
          setVendors(response.data.data);
        } catch (error) {
          console.error("Error fetching vendors:", error);
        } finally {
          setLoadingVendors(false);
        }
      }
    };

    fetchVendors();
  }, [user]);

  // Set form values when storeData changes
  React.useEffect(() => {
    form.setFieldsValue(storeData);
  }, [storeData, form]);

  // Add a new phone number field
  const addPhoneNumber = () => {
    const updatedPhones = [...storeData.contactPhones, ""];
    form.setFieldsValue({ contactPhones: updatedPhones });
    onFormChange({ contactPhones: updatedPhones });
  };

  // Handle phone number change
  const handlePhoneChange = (index, value) => {
    const updatedPhones = [...storeData.contactPhones];
    updatedPhones[index] = value;
    onFormChange({ contactPhones: updatedPhones });
  };

  // Add a new benefit field
  const addBenefit = () => {
    const updatedBenefits = [...storeData.benefits, ""];
    form.setFieldsValue({ benefits: updatedBenefits });
    onFormChange({ benefits: updatedBenefits });
  };

  // Handle benefit change
  const handleBenefitChange = (index, value) => {
    const updatedBenefits = [...storeData.benefits];
    updatedBenefits[index] = value;
    onFormChange({ benefits: updatedBenefits });
  };

  // Handle day toggle
  const handleDayToggle = (day, checked) => {
    const newOperationalDays = checked
      ? [...storeData.operationalDays, day]
      : storeData.operationalDays.filter((d) => d !== day);
    
    onFormChange({ operationalDays: newOperationalDays });
  };

  // Handle time change for a specific day
  const handleTimeChange = (day, field, time) => {
    const newOperatingHours = {
      ...storeData.operatingHours,
      [day]: {
        ...storeData.operatingHours[day],
        [field]: time,
      },
    };
    onFormChange({ operatingHours: newOperatingHours });
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      onValuesChange={(_, allValues) => onFormChange(allValues)}
    >
      {user?.role === "Admin" && !location.state && (
        <Item
          label="Select Vendor"
          name="vendor_id"
          rules={[{ required: true, message: "Please select a vendor" }]}
        >
          <Select
            loading={loadingVendors}
            placeholder="Select a vendor"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {vendors.map((vendor) => (
              <Select.Option key={vendor._id} value={vendor._id}>
                {`${vendor.first_name} ${vendor.last_name}`}
              </Select.Option>
            ))}
          </Select>
        </Item>
      )}

      <Item label="Store Name (English)" name="name">
        <Input
          placeholder="My Awesome Store"
          size="middle"
          readOnly={!canUpdate}
        />
      </Item>

      {/* Added French Name Field */}
      <Item label="Store Name (French)" name="name_fr">
        <Input
          placeholder="Mon Magasin Fantastique"
          size="middle"
          readOnly={!canUpdate}
        />
      </Item>

      <Item label="Store Description (English)" name="description">
        <Input.TextArea
          placeholder="Describe your store..."
          rows={3}
          size="middle"
          readOnly={!canUpdate}
        />
      </Item>

      {/* Added French Description Field */}
      <Item label="Store Description (French)" name="description_fr">
        <Input.TextArea
          placeholder="Décrivez votre magasin..."
          rows={3}
          size="middle"
          readOnly={!canUpdate}
        />
      </Item>

      <Item label="Contact Email" name="contactEmail">
        <Input
          placeholder="contact@store.com"
          size="middle"
          readOnly={!canUpdate}
        />
      </Item>

      {/* Dynamic Phone Numbers */}
      <Item label="Contact Phones">
        {storeData?.contactPhones?.map((phone, index) => (
          <Input
            key={index}
            placeholder="Enter phone number"
            value={phone}
            size="middle"
            onChange={(e) => handlePhoneChange(index, e.target.value)}
            style={{ marginBottom: "8px" }}
            readOnly={!canUpdate}
          />
        ))}
        <Button
          type="dashed"
          onClick={addPhoneNumber}
          block
          icon={<PlusOutlined />}
          disabled={!canUpdate}
        >
          Add Phone Number
        </Button>
      </Item>

      <Item label="Complete Address" name="address">
        <Input
          placeholder="Enter full address"
          size="middle"
          readOnly={!canUpdate}
        />
      </Item>

      <Item label="Delivery Radius" name="delivery_radius">
        <Input
          placeholder="Enter Delivery Radius in which you can deliver"
          type="number"
          step="0.1"
          size="middle"
          readOnly={!canUpdate}
        />
      </Item>

      {/* Operating Hours Section */}
      <Item label="Operating Hours">
        {daysOptions.map((day) => (
          <div key={day} style={{ marginBottom: 16 }}>
            <Checkbox
              checked={storeData.operationalDays.includes(day)}
              onChange={(e) => handleDayToggle(day, e.target.checked)}
              disabled={!canUpdate}
            >
              {day}
            </Checkbox>

            {storeData.operationalDays.includes(day) && (
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <TimePicker
                    use12Hours
                    format="h:mm A"
                    value={storeData.operatingHours[day]?.open}
                    onChange={(time) => handleTimeChange(day, "open", time)}
                    disabled={!canUpdate}
                    placeholder="Opening Time"
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col span={12}>
                  <TimePicker
                    use12Hours
                    format="h:mm A"
                    value={storeData.operatingHours[day]?.close}
                    onChange={(time) => handleTimeChange(day, "close", time)}
                    disabled={!canUpdate}
                    placeholder="Closing Time"
                    style={{ width: "100%" }}
                  />
                </Col>
              </Row>
            )}
          </div>
        ))}
      </Item>

      {/* Store Benefits */}
      <Item label="Store Benefits">
        {storeData?.benefits?.map((benefit, index) => (
          <Input
            key={index}
            placeholder="Enter store benefit"
            value={benefit}
            size="middle"
            onChange={(e) => handleBenefitChange(index, e.target.value)}
            style={{ marginBottom: "8px" }}
            readOnly={!canUpdate}
          />
        ))}
        <Button
          type="dashed"
          onClick={addBenefit}
          block
          icon={<PlusOutlined />}
          disabled={!canUpdate}
        >
          Add Store Benefit
        </Button>
      </Item>

      <Button
        htmlType="submit"
        loading={loading}
        type="primary"
        size="middle"
        className="w-full md:w-48 mt-2"
        disabled={!canUpdate && !canCreate}
      >
        {idExist ? "Update Store" : "Save Store"}
      </Button>
    </Form>
  );
};

export default StoreForm;